import React, { useCallback, useEffect, useMemo } from 'react';
import { NavigationContainer, DefaultTheme as NavDefaultTheme, DarkTheme as NavDarkTheme } from '@react-navigation/native';
import { Provider, useSelector } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { StatusBar } from 'expo-status-bar';
import { View, ActivityIndicator, Platform } from 'react-native';
import { SafeAreaProvider } from 'react-native-safe-area-context';

import { store, persistor } from './src/store';
import { navigationRef } from './src/navigation/navigationRef';
import { linkingOptions, documentTitle } from './src/navigation/linking';
import RootNavigator from './src/navigation/RootNavigator';
import { useTheme } from './src/hooks/useTheme';
import { Notification } from './src/components/Notification';
import { AblyProvider } from './src/contexts/AblyContext';
import { FolderScanJobProvider } from './src/contexts/FolderScanJobContext';
import FolderScanProgressChip from './src/components/FolderScanProgressChip';
import { UploadJobProvider } from './src/contexts/UploadJobContext';
import UploadProgressChip from './src/components/UploadProgressChip';
import { AccountPresenceProvider } from './src/contexts/AccountPresenceContext';
import { CarPlayProvider } from './src/contexts/CarPlayContext';
import ErrorBoundary from './src/components/ErrorBoundary';
import loadIcons from './src/icons';
import { installGlobalErrorHandlers } from './src/utilities/errorReporting';
import { NavigationStateProvider, useSetNavState } from './src/contexts/NavigationStateContext';
import DesktopLayout from './src/components/DesktopLayout';
import { getElectronBridge, isElectron, isWeb } from './src/utilities/platform';
import { syncToElectron as syncAllowScreenshotsToElectron } from './src/utilities/allowScreenshots';
import ShareIntentIngestionBridge from './src/components/ShareIntentIngestionBridge';
import DriveAccountChooser from './src/components/DriveAccountChooser';
import { SnapshotViewBannerHost } from './src/components/SnapshotViewBanner';
import { PartnerAccessBannerHost } from './src/components/PartnerAccessBanner';
import { subscribeWebPush } from './src/services/webPush';
import { redeemSignupHandoff } from './src/store/actions/auth';
import { startIapObserver, teardownIap } from './src/services/billing/iap';
import { fetchAccounts } from './src/store/actions/accounts';

let ShareIntentProvider: React.ComponentType<{ children: React.ReactNode }> = ({ children }) => <>{children}</>;
try {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const mod = require('expo-share-intent');
  if (mod?.ShareIntentProvider) ShareIntentProvider = mod.ShareIntentProvider;
} catch {
  // Native module is optional on web/desktop.
}

// Register all FontAwesome icons at app startup (mirrors web app's loadIcons() call)
loadIcons();

// Catch what the ErrorBoundary can't: non-render JS errors (ErrorUtils) and, on
// web, unhandled promise rejections. Reports flow to /monitoring/client-errors
// → Better Stack → Admin Console → System Health.
installGlobalErrorHandlers();

// On macOS Electron (titleBarStyle: 'hiddenInset'), the traffic-light buttons
// sit at ~y=8 and occupy the top ~28px of the window on the left side.
// SafeAreaProvider doesn't know about this natively on web, so we inject the
// correct top inset manually so that SafeAreaView and useSafeAreaInsets() return
// the right value — preventing headers and modal close buttons from being hidden
// behind the traffic lights.
const ELECTRON_SAFE_AREA_INSETS =
  isElectron && typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform || '')
    ? { top: 28, bottom: 0, left: 0, right: 0 }
    : undefined;

function AppContent() {
  const setNavState = useSetNavState();
  const authorized = useSelector((s: any) => !!s?.session?.authorized);
  const firstName = useSelector((s: any) => String(s?.session?.account?.first_name || '').trim());
  const themeMode = useSelector((s: any) => (s?.session?.theme === 'light' ? 'light' : 'dark'));
  // Give React Navigation a theme that matches the app. Without this it defaults
  // to the light DefaultTheme (colors.background = WHITE), which shows through as
  // a white border/flash around dark screens during modal transitions (e.g.
  // returning from the EllieVoice voice-chat modal).
  const theme = useTheme();
  const navTheme = useMemo(() => {
    const base = theme.dark ? NavDarkTheme : NavDefaultTheme;
    return {
      ...base,
      colors: {
        ...base.colors,
        background: theme.colors.background,
        card: theme.colors.card,
        text: theme.colors.text,
        border: theme.colors.border,
        primary: theme.colors.primary,
      },
    };
  }, [theme]);
  const proposedMap = useSelector((s: any) => s?.proposedActions?.list || {});
  const remindersMap = useSelector((s: any) => s?.reminders?.list || {});

  const handleStateChange = useCallback(
    (state: any) => setNavState(state),
    [setNavState],
  );

  // Register-by-chat signup handoff. The marketing embed opens the app at
  // /?signup_handoff=<ticket>. Handle it HERE (not just on LoginScreen) so it
  // works even when another account is already signed in on this browser:
  // redeemSignupHandoff redeems the ticket first, then signs out any existing
  // session and seeds the new account's force-change-password challenge. Once
  // that's done (and we're unauthenticated), route to ForcePassword. Web-only.
  useEffect(() => {
    if (!isWeb || typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const ticket = params.get('signup_handoff');
    if (!ticket) return;
    // Strip immediately so a refresh/back can't replay the single-use ticket.
    try {
      params.delete('signup_handoff');
      const qs = params.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash || ''}`);
    } catch { /* non-fatal */ }
    (async () => {
      const result: any = await (store.dispatch as any)(redeemSignupHandoff(ticket));
      if (!result?.success) return;
      // Navigate once the (now unauthenticated) navigator is ready — after a
      // sign-out the unauth stack needs a tick to mount ForcePassword.
      let tries = 0;
      const go = () => {
        if (navigationRef.isReady() && !store.getState()?.session?.authorized) {
          try { (navigationRef as any).navigate('ForcePassword', { email: result.email }); } catch { /* not ready yet */ }
          return;
        }
        if (tries++ < 60) setTimeout(go, 50);
      };
      go();
    })();
  }, []);

  // Push the AsyncStorage-backed "Allow Screenshots" preference to the Electron
  // main process once at startup. Renderer is the source of truth; main keeps a
  // mirror in preferences.json so it can apply setContentProtection BEFORE the
  // renderer loads next launch.
  useEffect(() => {
    if (!isElectron) return;
    syncAllowScreenshotsToElectron().catch(() => {});
  }, []);

  useEffect(() => {
    if (!isElectron) return;
    const bridge = getElectronBridge();
    if (!bridge?.setQuickActionsContext) return;

    const pendingActions = Object.values(proposedMap || {}).reduce((sum: number, list: any) => {
      if (!Array.isArray(list)) return sum;
      return sum + list.filter((a: any) => a?.status === 'pending').length;
    }, 0);

    const activeReminders = Object.values(remindersMap || {}).reduce((sum: number, list: any) => {
      if (!Array.isArray(list)) return sum;
      return sum + list.filter((r: any) => r?.status !== 'sent' && r?.status !== 'canceled').length;
    }, 0);

    bridge.setQuickActionsContext({
      authorized,
      firstName,
      pendingActions,
      activeReminders,
      themeMode,
    });
  }, [authorized, firstName, proposedMap, remindersMap, themeMode]);

  // ── Web push auto-subscribe ────────────────────────────────────────────────
  // Once the user is signed in on web/electron, opportunistically subscribe to
  // push notifications. The flow is idempotent: if a SW subscription already
  // exists and the backend has it on file, this is a near-zero-cost no-op. If
  // the user denied permission previously the call exits without re-prompting.
  // Native iOS/Android skip via the `isWeb` guard — they use APNs/FCM through
  // expo-notifications elsewhere.
  useEffect(() => {
    if (!isWeb || !authorized) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await subscribeWebPush();
        if (cancelled) return;
        if (!res.success && !res.unsupported && !res.denied) {
          // Soft-warn in dev; we don't toast on web since the legacy app's
          // toastr isn't wired up here and a missed subscription isn't
          // user-actionable from the chat surface anyway.
          if (typeof console !== 'undefined') {
            console.warn('[webPush] subscribe failed:', res.message);
          }
        }
      } catch (err) {
        if (!cancelled && typeof console !== 'undefined') {
          console.warn('[webPush] subscribe threw', err);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [authorized]);

  // ── Stranded IAP transaction sync (iOS) ────────────────────────────────────
  // Open the StoreKit connection once signed in so transactions StoreKit
  // re-delivers on launch — purchases the app never finalized with billing
  // (killed mid-purchase, confirmation timeout, offer-code redemptions) —
  // reach the purchase listener and get synced + finished. Without this, a
  // stranded purchase leaves the user charged by Apple with no plan on their
  // account, and every retry dead-ends on "Item already owned".
  useEffect(() => {
    if (Platform.OS !== 'ios' || !authorized) return;
    // startIapObserver is async: if authorized flips false while its
    // connection setup is still in flight, the cleanup below runs against
    // nothing and the resolving observer would then attach listeners for a
    // signed-out session. The cancelled flag closes that race — a start that
    // lands after cleanup tears itself straight back down.
    let cancelled = false;
    startIapObserver(() => {
      // A stranded purchase just landed on the account — refresh so plan
      // gates and the billing screen reflect it without a manual reload.
      const s = store.getState();
      const accountId = s?.session?.account?.account_id;
      const cognitoId = s?.authentication?.user?.cognito_id;
      if (accountId && cognitoId) void (store.dispatch as any)(fetchAccounts(accountId, cognitoId));
    }).then(() => {
      if (cancelled) void teardownIap();
    }).catch(() => {});
    // Tear down on sign-out (authorized → false re-runs this effect): the
    // purchase listeners capture the store, so leaving them attached would
    // keep syncing StoreKit re-deliveries against whichever account signs
    // in next. ensureIapConnection re-opens on demand after re-auth.
    return () => {
      cancelled = true;
      void teardownIap();
    };
  }, [authorized]);

  return (
    <SafeAreaProvider initialMetrics={ELECTRON_SAFE_AREA_INSETS ? { insets: ELECTRON_SAFE_AREA_INSETS, frame: { x: 0, y: 0, width: 0, height: 0 } } : undefined}>
      <StatusBar style="auto" />
      <AblyProvider>
       <FolderScanJobProvider>
       <UploadJobProvider>
       <AccountPresenceProvider>
        {/* Navigation-level boundary — catches crashes inside screens/navigators */}
        <ErrorBoundary boundary="NavigationContainer">
          <DesktopLayout>
            <SnapshotViewBannerHost>
             <PartnerAccessBannerHost>
              <NavigationContainer
                ref={navigationRef}
                theme={navTheme}
                onStateChange={handleStateChange}
                linking={linkingOptions}
                documentTitle={documentTitle}
              >
                <RootNavigator />
              </NavigationContainer>
             </PartnerAccessBannerHost>
            </SnapshotViewBannerHost>
          </DesktopLayout>
        </ErrorBoundary>
        <ShareIntentIngestionBridge />
        <DriveAccountChooser />
        <Notification />
        <FolderScanProgressChip />
        <UploadProgressChip />
       </AccountPresenceProvider>
       </UploadJobProvider>
       </FolderScanJobProvider>
      </AblyProvider>
    </SafeAreaProvider>
  );
}

// Top-level boundary — catches crashes before/during Redux hydration
export default function App() {
  return (
    <ErrorBoundary boundary="AppRoot">
      <Provider store={store}>
        <PersistGate
          loading={
            <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
              <ActivityIndicator size="large" />
            </View>
          }
          persistor={persistor}
        >
          {/* NavigationStateProvider broadcasts nav state to DesktopSidebar */}
          <NavigationStateProvider>
            <ShareIntentProvider>
              <CarPlayProvider>
                <AppContent />
              </CarPlayProvider>
            </ShareIntentProvider>
          </NavigationStateProvider>
        </PersistGate>
      </Provider>
    </ErrorBoundary>
  );
}
